/**
 * Sticker maker pixel helpers — everything stays on the device (no cloud
 * service, no ML kit). Port of the native app's `lib/src/media/cutout.dart`.
 *
 * PIXEL FORMAT DIFFERENCE (read before touching the alpha code):
 * Flutter's raw RGBA is PREMULTIPLIED, so the phone writes transparent pixels
 * as all-zero bytes and its 1-px soft edge halves all four channels. Canvas
 * `ImageData` on the web is STRAIGHT (non-premultiplied) alpha: halving the
 * colour too would darken the edge into a grey fringe once the browser
 * premultiplies it for display. So here the soft edge halves ONLY the alpha
 * byte, and removed pixels are written as rgba(0,0,0,0) — transparent black,
 * which is also what the browser stores for alpha 0 anyway.
 */

/** Sticker canvas side in logical units: the exported PNG is 512×512. */
export const STICKER_SIDE = 512;

export type Rgba = Uint8ClampedArray | Uint8Array;

export interface CutoutResult {
  rgba: Uint8ClampedArray;
  /** Share of the picture that was removed (0..1). */
  share: number;
}

/**
 * Remove a plain background: flood-fill from every border pixel through
 * pixels whose colour is within `tolerance` of the colour where the fill
 * started, then soften the edge by one pixel. Returns a new buffer (the input
 * is untouched) and how much of the picture was removed.
 */
export function floodCutout(rgba: Rgba, width: number, height: number, tolerance = 42): CutoutResult {
  const out = new Uint8ClampedArray(rgba);
  const n = width * height;
  const removed = new Uint8Array(n); // 1 = background
  const seedColor = new Int32Array(n); // seed index per pixel (for colour reference)
  // FIFO queue: every pixel is enqueued at most once, so n slots suffice.
  const queue = new Int32Array(n);
  let head = 0;
  let tail = 0;
  const tol2 = tolerance * tolerance * 3;
  const stepTol2 = Math.floor(tol2 / 6); // Dart `tol2 ~/ 6`
  const driftTol2 = tol2 * 4;

  const dist2 = (a: number, b: number): number => {
    const dr = rgba[a * 4]! - rgba[b * 4]!;
    const dg = rgba[a * 4 + 1]! - rgba[b * 4 + 1]!;
    const db = rgba[a * 4 + 2]! - rgba[b * 4 + 2]!;
    return dr * dr + dg * dg + db * db;
  };

  const seed = (i: number) => {
    if (removed[i] === 1) return;
    removed[i] = 1;
    seedColor[i] = i;
    queue[tail++] = i;
  };

  for (let x = 0; x < width; x++) {
    seed(x);
    seed((height - 1) * width + x);
  }
  for (let y = 0; y < height; y++) {
    seed(y * width);
    seed(y * width + width - 1);
  }

  const visit = (j: number, i: number, ref: number) => {
    if (removed[j] === 1) return;
    // Close to the seed colour, or a small step from the neighbour (smooth
    // gradients like a sky) — but never drifting further than twice the
    // tolerance from the seed: a soft JPEG edge is also "small steps", and
    // must not lead the fill into a uniform subject.
    const fromSeed = dist2(j, ref);
    if (fromSeed <= tol2 || (fromSeed <= driftTol2 && dist2(j, i) <= stepTol2)) {
      removed[j] = 1;
      seedColor[j] = ref;
      queue[tail++] = j;
    }
  };

  while (head < tail) {
    const i = queue[head++]!;
    const x = i % width;
    const y = (i - x) / width;
    const ref = seedColor[i]!;
    // Same neighbour order as the phone (left, right, up, down) → same result.
    if (x > 0) visit(i - 1, i, ref);
    if (x < width - 1) visit(i + 1, i, ref);
    if (y > 0) visit(i - width, i, ref);
    if (y < height - 1) visit(i + width, i, ref);
  }

  let count = 0;
  for (let i = 0; i < n; i++) {
    if (removed[i] === 1) {
      out.fill(0, i * 4, i * 4 + 4); // transparent = rgba(0,0,0,0)
      count++;
    }
  }
  // Soft edge: foreground pixels touching the background get half alpha
  // (straight alpha: colour bytes stay as they are — see the header note).
  for (let i = 0; i < n; i++) {
    if (removed[i] === 1) continue;
    const x = i % width;
    const y = (i - x) / width;
    const edge =
      (x > 0 && removed[i - 1] === 1) ||
      (x < width - 1 && removed[i + 1] === 1) ||
      (y > 0 && removed[i - width] === 1) ||
      (y < height - 1 && removed[i + width] === 1);
    if (edge) out[i * 4 + 3] = out[i * 4 + 3]! >> 1;
  }
  return { rgba: out, share: n > 0 ? count / n : 0 };
}

/** Below / above this removed share, the remover found nothing sensible to cut. */
export function cutoutLooksWrong(share: number): boolean {
  return share < 0.05 || share > 0.97;
}

export const CUTOUT_FAILED_MESSAGE =
  "Couldn't find a plain background to remove — use the eraser instead.";

/**
 * Erase a disc (radius `r` px) around (`cx`, `cy`) in place; with `restore`
 * the original pixels come back instead. Erasing clears all four bytes.
 * Returns true when at least one pixel was inside the picture.
 */
export function eraseDisc(
  rgba: Rgba,
  width: number,
  height: number,
  cx: number,
  cy: number,
  r: number,
  restore?: Rgba | null,
): boolean {
  if (cx + r < 0 || cy + r < 0 || cx - r > width - 1 || cy - r > height - 1) return false;
  const r2 = r * r;
  const x0 = clamp(Math.floor(cx - r), 0, width - 1);
  const x1 = clamp(Math.ceil(cx + r), 0, width - 1);
  const y0 = clamp(Math.floor(cy - r), 0, height - 1);
  const y1 = clamp(Math.ceil(cy + r), 0, height - 1);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const dx = x - cx;
      const dy = y - cy;
      if (dx * dx + dy * dy > r2) continue;
      const i = (y * width + x) * 4;
      if (restore) {
        rgba[i] = restore[i]!;
        rgba[i + 1] = restore[i + 1]!;
        rgba[i + 2] = restore[i + 2]!;
        rgba[i + 3] = restore[i + 3]!;
      } else {
        rgba[i] = 0;
        rgba[i + 1] = 0;
        rgba[i + 2] = 0;
        rgba[i + 3] = 0;
      }
    }
  }
  return true;
}

/** An existing sticker (already transparent somewhere) counts as cut out. */
export function hasTransparency(rgba: Rgba): boolean {
  for (let i = 3; i < rgba.length; i += 4 * 97) {
    if (rgba[i]! < 250) return true;
  }
  return false;
}

export interface Placement {
  /** Zoom on top of "fit the picture in the canvas" (1 = fit). */
  scale: number;
  /** Picture centre offset from the canvas centre, in canvas units. */
  offsetX: number;
  offsetY: number;
}

/**
 * Like WhatsApp: after a cut-out the subject fills the sticker, centred, with
 * room for the outline. Bounding box of alpha > 24 scaled to 86% of the
 * canvas side. Null when nothing is left.
 */
export function fitToSubject(rgba: Rgba, w: number, h: number, side = STICKER_SIDE): Placement | null {
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      if (rgba[(row + x) * 4 + 3]! <= 24) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < x0 || y1 < y0) return null;
  const base = Math.min(side / w, side / h); // canvas units per picture px at scale 1
  const scale = clamp((0.86 * side) / (Math.max(x1 - x0 + 1, y1 - y0 + 1) * base), 0.3, 4);
  const fit = base * scale;
  return {
    scale,
    offsetX: -((x0 + x1 + 1) / 2 - w / 2) * fit,
    offsetY: -((y0 + y1 + 1) / 2 - h / 2) * fit,
  };
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
